import { ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowRightIcon,
  BoltIcon,
  Button,
  CalendarDayIcon,
  ClockIcon,
  IconProps,
  Paragraph,
} from '@quickmd-llc/design-system';
import { Events, tracking } from '@lib/events';
import { TimeBlock } from './context/utils';
import { useAppointmentTimePreference } from './context/useAppointmentTimePreference';
import dayjs from 'dayjs';

type Props = {
  timeBlock: TimeBlock;
};

const descriptionByTimeBlock: Record<
  TimeBlock,
  { description: string; Icon: ComponentType<IconProps> }
> = {
  now: { description: 'Be seen within 30 min', Icon: BoltIcon },
  today: { description: 'Later today', Icon: ClockIcon },
  tomorrow: { description: 'Tomorrow', Icon: CalendarDayIcon },
};

export function TimeBlockCard({ timeBlock }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { groupedAvailabilitiesByTimeBlock } = useAppointmentTimePreference();

  const { description, Icon } = descriptionByTimeBlock[timeBlock];
  const date = getDate(timeBlock);

  function handleClick() {
    if (!groupedAvailabilitiesByTimeBlock) {
      return null;
    }

    const { now, today, tomorrow } = groupedAvailabilitiesByTimeBlock;

    const params = new URLSearchParams(searchParams);
    params.set('timeBlock', timeBlock);

    setSearchParams(params);

    tracking.trackEvent(Events.SELECT_TIME_BLOCK, {
      availableTimeBlocks: {
        now: Boolean(now.length),
        today: Boolean(today.length),
        tomorrow: Boolean(tomorrow.length),
      },
      timeBlock,
    });
  }

  return (
    <div
      className="group flex cursor-pointer items-center justify-between gap-sp-2 rounded-lg border border-border-medium bg-surface-default p-sp-3 hover:border-border-highlight hover:bg-surface-active"
      onClick={handleClick}
      data-testid={`pw_appointmentTime_${timeBlock}`}
    >
      <div className="flex items-center gap-sp-2">
        <Icon className="text-text-highlight" iconSize="xl" />

        <div className="grid gap-sp-0.5">
          <Paragraph className="font-semibold text-text-dark group-hover:text-text-highlight">
            {description}
          </Paragraph>

          {date && (
            <Paragraph className="text-text-light" size="xs">
              {date}
            </Paragraph>
          )}
        </div>
      </div>

      <Button isIcon onClick={handleClick} priority="primary" size="s">
        <ArrowRightIcon />
      </Button>
    </div>
  );
}

function getDate(timeBlock: TimeBlock) {
  if (timeBlock === 'now') {
    return;
  }

  const now = dayjs();

  if (timeBlock === 'today') {
    return now.format('MMM D');
  }

  return now.add(1, 'day').format('MMM D');
}
